import Hls from "hls.js"

const CIRC = 2 * Math.PI * 18 // stroke-dasharray for r=18

type HookSelf = {
  el: HTMLVideoElement
  pushEvent: (event: string, payload: object) => void
  _hls: Hls | null
  _fired: boolean
  _started: boolean
  _interval: ReturnType<typeof setInterval> | null
}

function fmtTime(s: number): string {
  const m = Math.floor(s / 60)
  const sec = s % 60
  return `${m}:${String(sec).padStart(2, "0")}`
}

function updateArc(remaining: number, limit: number) {
  const timeEl = document.getElementById("preview-time-text")
  const arcEl = document.getElementById("preview-arc")
  if (timeEl) timeEl.textContent = fmtTime(remaining)
  if (arcEl) {
    const offset = CIRC * (1 - remaining / limit)
    arcEl.setAttribute("stroke-dashoffset", String(offset))
  }
}

export const PreviewTimer: Record<string, Function> = {
  mounted(this: HookSelf) {
    const video = this.el
    const src = video.dataset.src ?? ""
    const limit = parseInt(video.dataset.previewLimit ?? "90", 10)
    this._hls = null
    this._fired = false
    this._started = false
    this._interval = null

    const enforceLimit = () => {
      if (!this._fired && video.currentTime >= limit) {
        this._fired = true
        video.pause()
        video.currentTime = limit
        this.pushEvent("preview_ended", {})
      }
    }

    video.addEventListener("playing", () => {
      if (!this._started) {
        this._started = true
        this.pushEvent("preview_started", {})

        // Drive the countdown arc directly from JS — server tick is a backup
        let remaining = limit
        updateArc(remaining, limit)
        this._interval = setInterval(() => {
          remaining = Math.max(0, remaining - 1)
          updateArc(remaining, limit)
          if (remaining <= 0) {
            clearInterval(this._interval!)
            this._interval = null
          }
        }, 1000)
      }
    })

    video.addEventListener("timeupdate", enforceLimit)

    if (src.includes(".m3u8")) {
      if (Hls.isSupported()) {
        const hls = new Hls({ startLevel: -1, maxBufferLength: 15 })
        this._hls = hls
        hls.loadSource(src)
        hls.attachMedia(video)
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.play().catch(() => {})
        })
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = src
        video.play().catch(() => {})
      }
    } else {
      video.src = src
    }
  },

  destroyed(this: HookSelf) {
    if (this._interval) {
      clearInterval(this._interval)
      this._interval = null
    }
    this._hls?.destroy()
    this._hls = null
  },
}
